import { RoomType } from '@/shared/types';

/** A draft item is one explicitly marked 'draft'. Absent status === published. */
export const isDraft = (item: { status?: 'draft' | 'published' }): boolean =>
  item.status === 'draft';

/** Published === not a draft (absent status counts as published, for back-compat). */
export const isPublished = (item: { status?: 'draft' | 'published' }): boolean =>
  item.status !== 'draft';

/**
 * Keeps only rooms that should count towards a residence's public starting price:
 * published rooms with a non-empty price list where every tier is > 0. Draft or
 * partially-filled rooms are excluded so they never pollute `startingPrice`.
 */
export const pricedPublishedRooms = <T extends Pick<RoomType, 'priceByOccupancy' | 'status'>>(
  rooms: T[],
): T[] =>
  rooms.filter(
    (rt) =>
      isPublished(rt) &&
      Array.isArray(rt.priceByOccupancy) &&
      rt.priceByOccupancy.length > 0 &&
      rt.priceByOccupancy.every((p) => p > 0),
  );

/**
 * Returns the lowest price tier for a room type.
 */
export const getMinRoomTypePrice = (rt: Pick<RoomType, 'priceByOccupancy'>): number => {
  return Math.min(...rt.priceByOccupancy);
};

/**
 * Calculates the minimum price from a list of room types.
 * Returns null if there are no room types, or no finite price (e.g. only
 * draft/price-less rooms were passed in).
 */
export const calculateMinimumPrice = (roomTypes: Array<Pick<RoomType, 'priceByOccupancy'>>): number | null => {
  if (roomTypes.length === 0) {
    return null;
  }

  const prices = roomTypes.map(getMinRoomTypePrice);
  const min = Math.min(...prices);
  return Number.isFinite(min) ? min : null;
};

/**
 * Calculates the minimum stay from a list of room types.
 * Returns 1 if there are no room types.
 */
export const calculateMinimumStay = (roomTypes: Array<{ minStay: number }>): number => {
  if (roomTypes.length === 0) {
    return 1;
  }
  
  const minStays = roomTypes.map(rt => rt.minStay);
  return Math.min(...minStays);
};

